import { wallToIso } from './time'
// Contrato con Protect.Api (§17.1). Los tipos son los DTOs del backend; un cambio incompatible
// va a /api/command/v2.

export type Connectivity = 'Online' | 'Stale' | 'Offline' | 'Never'

export interface UnitView {
  patrolUnitId: string
  callSign: string
  division: string
  status: string
  connectivity: Connectivity
  latitude: number | null
  longitude: number | null
  positionAt: string | null
  lastSeenAt: string | null
  currentAssignmentId: string | null
  /** Por qué está fuera de servicio (el último reporte), si lo está. */
  statusReason?: string
}

/** Lo que dejó una cancelación a mano (H-128). */
export interface ManualCancellation {
  planId: string
  version: number
  assignmentId: string
  callSign: string
  label: string
}

/** Lo que dejó una reasignación manual. */
export interface ManualReassignment {
  planId: string
  version: number
  originalAssignmentId: string
  originalStatus: string
  newAssignmentId: string
  toCallSign: string
  plannedArrivalAt: string
}

export interface AssignmentView {
  assignmentId: string
  patrolUnitId: string
  callSign: string
  sequence: number
  latitude: number
  longitude: number
  label: string
  plannedArrivalAt: string
  plannedDepartureAt: string
  status: string
  checkedInAt: string | null
  checkedOutAt: string | null
}

export type AlertStatus = 'New' | 'Acknowledged' | 'Assigned' | 'Escalated' | 'Resolved' | 'Closed'
export type AlertAction = 'Acknowledge' | 'Assign' | 'Escalate' | 'Resolve' | 'Close'

export interface Alert {
  alertId: string
  kind: string
  severity: 'Warning' | 'Critical'
  patrolUnitId: string
  callSign: string
  assignmentId: string | null
  message: string
  /** Los datos de la explicación: la frase se arma en el idioma del operador. */
  args: Record<string, string> | null
  status: AlertStatus
  assignedTo: string
  createdAt: string
  updatedAt: string
}

export type IncidentStatus = 'Reported' | 'Validated' | 'Active' | 'Controlled' | 'Completed' | 'Cancelled'
export type IncidentAction = 'Validate' | 'Activate' | 'Control' | 'Complete' | 'Cancel'

export interface Incident {
  incidentId: string
  patrolUnitId: string
  callSign: string
  description: string
  latitude: number | null
  longitude: number | null
  status: IncidentStatus
  reportedAt: string
  completedAt: string | null
}

/** Unidades simuladas operando. Solo existe en un despliegue de demostración. */
export interface Simulation {
  running: boolean
  units: string[]
  deviating: string[]
  startedAt: string | null
  visits: number
  log: string[]
}

export interface SimulationRequest {
  units: number
  paceSeconds: number
  deviateEvery: number
  shiftCode: string
}

/** Un rechazo del backend: el código manda, el texto es respaldo (Protect.Contracts.Common). */
export interface Denial {
  code: string
  reason: string
  args?: Record<string, string> | null
  /**
   * El identificador de la acción que falló, tal como lo devolvió el servidor (§13.1).
   *
   * Es lo que alguien le dicta a soporte: con él se encuentra en el registro la petición y todo
   * lo que disparó por la cola. Sin él, la única pista es la hora.
   */
  reference?: string
}

export class DeniedError extends Error {
  constructor(readonly denial: Denial) {
    super(denial.reason)
  }
}

export interface Connection {
  server: string
  tenantId: string
  agencyId: string
  user: string

  /**
   * Los roles con los que se entra, en el ingreso de demostración.
   *
   * Son varios porque en una agencia chica la misma persona lleva dos sombreros (§7), y porque
   * una demostración necesita pasar de jefe a analista sin pedirle a nadie que abra una consola.
   * Con el proveedor de identidad real esto no se elige: viene en el token.
   */
  roles: string[]
}

/**
 * Los roles que abren alguna pantalla del Centro de Mando.
 *
 * `officer` no está: su única capacidad es trabajo de calle y entra por el teléfono, así que
 * ofrecerlo aquí sería ofrecer un ingreso a ninguna parte.
 */
export const WEB_ROLES = [
  'chief', 'watch-commander', 'command', 'planner', 'analyst', 'fleet', 'hr', 'admin',
] as const

/** Cliente de la API del Centro de Mando. El token se pasa en cada llamada, nunca se guarda aquí. */
export class CommandApi {
  constructor(private readonly token: () => string | null) {}

  units = () => this.get<UnitView[]>('units')
  /** Las zonas de patrullaje con su forma (§10.1), para el mapa. */
  patrolZones = () => this.get<GeoJSON.FeatureCollection>('patrol-zones')
  assignments = () => this.get<AssignmentView[]>('assignments')
  alerts = (all = false) => this.get<Alert[]>(`alerts${all ? '?all=true' : ''}`)
  incidents = () => this.get<Incident[]>('incidents')

  applyAlert = (alertId: string, action: AlertAction, note = '', assignedTo = '') =>
    this.post<Alert>(`alerts/${alertId}/actions`, { action, note, assignedTo })

  applyIncident = (incidentId: string, action: IncidentAction, note = '') =>
    this.post<Incident>(`incidents/${incidentId}/actions`, { action, note })

  /** Un incidente que la unidad reportó por radio (H-63). */
  registerIncident = (patrolUnitId: string, description: string) =>
    this.post<Incident>('incidents', { patrolUnitId, description })

  /** La unidad queda fuera de servicio, con motivo, o vuelve (H-62). */
  setUnitAvailability = (patrolUnitId: string, available: boolean, reason = '') =>
    this.post<{ patrolUnitId: string; available: boolean }>(`units/${patrolUnitId}/availability`, { available, reason })

  shiftNow = (): Promise<ShiftNow> => this.get('shift')

  /** El libro de guardia de las últimas `hours` horas. */
  timeline = (hours: number, unit = ''): Promise<Timeline> => {
    const to = new Date()
    const from = new Date(to.getTime() - hours * 3600_000)
    return this.get(`timeline?from=${from.toISOString()}&to=${to.toISOString()}${unit ? `&unit=${unit}` : ''}`)
  }

  handoverPreview = (shiftDate: string, shiftCode: string): Promise<HandoverSnapshot> =>
    this.get(`handovers/preview?shiftDate=${shiftDate}&shiftCode=${shiftCode}`)

  /** Nulo si ese turno todavía no se ha entregado (204), que no es un error sino el caso normal. */
  handover = (shiftDate: string, shiftCode: string): Promise<Handover | null> =>
    this.get<Handover | null>(`handovers/${shiftDate}/${shiftCode}`)

  deliverHandover = (shiftDate: string, shiftCode: string, notes: string): Promise<Handover> =>
    this.post('handovers', { shiftDate, shiftCode, notes })

  receiveHandover = (handoverId: string, note: string): Promise<Handover> =>
    this.post(`handovers/${handoverId}/receipt`, { note })

  reoptimize = (shiftDate: string, shiftCode: string, reason: string) =>
    this.post<{ requestId: string }>('reoptimizations', { shiftDate, shiftCode, reason })

  // --- Demostración. En producción estos endpoints no existen y el panel no se muestra.

  simulation = () => this.call<Simulation>('GET', '/api/dev/simulation')
  startSimulation = (request: SimulationRequest) => this.call<Simulation>('POST', '/api/dev/simulation', request)
  stopSimulation = () => this.call<Simulation>('DELETE', '/api/dev/simulation')

  /** Arma todas las unidades del turno, como lo hacía el comando `tactix form`. */
  formShift = (shiftDate: string, shiftCode: string) =>
    this.call<{ formed: number; skipped: string[] }>('POST', '/api/dev/form-shift', { shiftDate, shiftCode })

  private get = <T>(path: string) => this.send<T>(path, undefined)
  private post = <T>(path: string, body: unknown) => this.send<T>(path, body)

  private send = <T>(path: string, body: unknown) =>
    this.call<T>(body === undefined ? 'GET' : 'POST', `/api/command/v1/${path}`, body)

  private async call<T>(method: string, url: string, body?: unknown): Promise<T> {
    const token = this.token()
    const response = await traced(url, {
      method,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    })
    // 204: no hay nada que mostrar todavía (un turno sin entregar); se lee como nulo.
    if (response.status === 204) return null as T
    if (response.ok) return (await response.json()) as T
    throw new DeniedError(await denialOf(response))
  }
}

/** Un conjunto de hotspots (ATLAS). */
export interface HotspotSet {
  setId: string
  name: string
  version: number
  status: 'Draft' | 'Candidate' | 'Published' | 'Retired'
  method: string
  methodVersion: string
  timeWindow: string
  periodStart: string
  periodEnd: string
  hotspotCount: number
  inputCount: number
  fingerprint: string
  createdBy: string
  createdAt: string
  approvedBy: string | null
  publishedAt: string | null
  /** Cuántos se pidieron y el mínimo de incidentes por lugar: explican por qué salieron menos (H-90). */
  requested?: number | null
  minIncidents?: number | null
}

export interface Hotspot {
  hotspotId: string
  rank: number
  latitude: number
  longitude: number
  incidentCount: number
  riskScore: number
  priority: 'High' | 'Medium' | 'Low'
  dwellMinutes: number
  jurisdiction: string
  topOffense: string
  hourlyProfile: number[]
}

export interface HotspotSetDetail {
  set: HotspotSet
  hotspots: Hotspot[]
}

/** Un hotspot, reducido a lo que hace falta para contar en qué cambió un conjunto. */
export interface HotspotBrief {
  hotspotId: string
  rank: number
  latitude: number
  longitude: number
  riskScore: number
  incidentCount: number
  jurisdiction: string
  topOffense: string
}

export interface HotspotMove {
  before: HotspotBrief
  after: HotspotBrief
  rankDelta: number
  riskDelta: number
  metersApart: number
}

/** En qué cambió un conjunto respecto de otro (§8.1). */
export interface SetChanges {
  before: HotspotSet
  after: HotspotSet
  /** Los parámetros de cada corrida, aplanados: la pantalla muestra solo lo que difiere. */
  parametersBefore: Record<string, string>
  parametersAfter: Record<string, string>
  shared: number
  entered: number
  left: number
  /** Jaccard en porcentaje: compartidos sobre la unión. */
  stability: number
  /** Qué parte del conjunto nuevo ya estaba en el anterior. */
  persistence: number
  /** Si las retículas coinciden. Cuando no, el emparejamiento es por cercanía. */
  sameGrid: boolean
  matchRadiusMeters: number
  new: HotspotBrief[]
  dropped: HotspotBrief[]
  moved: HotspotMove[]
}

/** Un turno en el que un vehículo salió a la calle. */
export interface VehicleShift {
  shiftDate: string
  shiftCode: string
  callSign: string
  division: string
  unitTypeCode: string
  status: string
  crew: string[]
  assignments: number
  /** Un vehículo conformado que no completó nada estuvo asignado en el papel, no en la calle. */
  completed: number
  formedBy: string
  formedAt: string
}

/** Dónde ha andado un vehículo (§9.2). */
export interface VehicleHistory {
  vehicle: Vehicle
  shiftsWorked: number
  totalAssignments: number
  totalCompleted: number
  firstShift: string | null
  lastShift: string | null
  /** Del más reciente al más antiguo. */
  shifts: VehicleShift[]
}

export interface Source {
  key: string
  name: string
  agencyId: string
}

export interface IngestionRun {
  /** Por qué falló, si falló (H-87). */
  error?: string | null
  runId: string
  source: string
  status: string
  startedAt: string
  finishedAt: string | null
  rowsRead: number
  rowsWritten: number
  rowsSkipped: number
  /**
   * El desglose por tipo de problema.
   *
   * Antes esto era `summary: string`, el JSON crudo que dejó la corrida, y la pantalla no lo
   * abría: se informaba cuántas filas se omitieron y nunca por qué.
   */
  quality: IngestionQuality
}

export interface IngestionQuality {
  /** Por qué no entró cada fila omitida: `no_external_id`, `unreadable`. */
  bySkip: Record<string, number>
  /** Qué le falta a las que sí entraron: `MissingGeo`, `ApproximateTime`… */
  byFlag: Record<string, number>
  byQuality: Record<string, number>
}

export interface CalculateRequest {
  name: string
  from: string
  to: string
  method: string
  window: string
  top: number
}

/** Lo que hace el Crime Analyst (§7). */
export class AtlasApi {
  constructor(private readonly token: () => string | null) {}

  sources = (): Promise<Source[]> => this.call('/api/atlas/v1/sources')

  ingestions = (): Promise<IngestionRun[]> => this.call('/api/atlas/v1/ingestions')

  ingest = (source: string, since: string, until: string): Promise<{ runId: string }> =>
    this.call('/api/atlas/v1/ingestions', 'POST', {
      source,
      since: since || null,
      until: until || null,
    })

  quality = (): Promise<QualityReport> => this.call('/api/atlas/v1/quality')

  incidents = (f: IncidentFilter): Promise<IncidentPage> => {
    // Solo lo que el analista puso: un parámetro vacío cambia la consulta del servidor sin que
    // nadie lo haya pedido.
    const q = new URLSearchParams()
    if (f.from) q.set('from', f.from)
    if (f.to) q.set('to', f.to)
    if (f.tier) q.set('tier', f.tier)
    if (f.quality) q.set('quality', f.quality)
    if (f.window && f.window !== 'ALL_DAY') q.set('window', f.window)
    if (f.limit) q.set('limit', String(f.limit))
    return this.call(`/api/atlas/v1/incidents?${q.toString()}`)
  }

  sets = (): Promise<HotspotSet[]> => this.call('/api/atlas/v1/hotspot-sets')

  detail = (setId: string): Promise<HotspotSetDetail> =>
    this.call(`/api/atlas/v1/hotspot-sets/${setId}`)

  calculate = (r: CalculateRequest): Promise<HotspotSetDetail> =>
    this.call('/api/atlas/v1/hotspot-sets', 'POST', r)

  publish = (setId: string): Promise<HotspotSet> =>
    this.call(`/api/atlas/v1/hotspot-sets/${setId}/publish`, 'POST', {})

  /** Contra el anterior de la misma serie, o contra el que se le indique. */
  changes = (setId: string, against?: string): Promise<SetChanges> =>
    this.call(`/api/atlas/v1/hotspot-sets/${setId}/changes${against ? `?against=${against}` : ''}`)

  private async call<T>(path: string, method = 'GET', body?: unknown): Promise<T> {
    const response = await traced(path, {
      method,
      headers: {
        Authorization: `Bearer ${this.token() ?? ''}`,
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    })
    if (!response.ok) throw new DeniedError(await denialOf(response))
    return (await response.json()) as T
  }
}

/** El turno visto por el Planner (§7): lo que hay y lo que ya está conformado. */
export interface ShiftSummary {
  shiftDate: string
  shiftCode: string
  /** La última propuesta devuelta, si es lo último que pasó con el plan del turno. */
  returned?: { version: number; proposedBy: string; decidedBy: string; decidedAt: string | null; reason: string } | null
  available: { officers: number; vehicles: number; required: number }
  units: {
    patrolUnitId: string; callSign: string; division: string
    unitTypeCode: string; vehicleId: string; crewSize: number; status: string
  }[]
  plan: {
    planId: string; version: number; status: string; proposedBy: string
    approvedBy: string | null; publishedAt: string | null
    assignments: number; byStatus: Record<string, number>
  } | null
  proposed: { planId: string; version: number; status: string; proposedBy: string; scenarioId: string } | null
  /** Si cada división alcanza a cubrir su turno. Vacío si la agencia no tiene dotación cargada. */
  coverage: DivisionCoverage[]
}

export interface DivisionCoverage {
  division: string
  positions: number
  requiredUnits: number
  requiredSergeants: number
  officers: number
  sergeants: number
  restricted: number
  absent: number
  vehicles: number
  formed: number
  /** Qué falta, en la forma «officers:3». Vacío es que alcanza. */
  shortfalls: string[]
}

export interface ScenarioKpis {
  coverage: number
  riskWeightedCoverage: number
  visitRating: number
  utilization: number
  visits: number
  expectedVisits: number
  hotspotsNotServed: number
  crossDivisionVisits: number
}

export interface Scenario {
  scenarioId: string
  label: string
  engineVersion: string
  createdBy: string
  createdAt: string
  isPartial: boolean
  units: number
  kpis: ScenarioKpis
  /** Las perillas con las que se corrió. Se comparan campo por campo. */
  policy: Record<string, string>
  /** Los hotspots sin ninguna visita, por riesgo: lo que se acepta al firmar un parcial (H-37). */
  uncovered?: UncoveredHotspot[]
}

/** Un hotspot que el plan deja sin visita. */
export interface UncoveredHotspot {
  label: string
  rank: number
  division: string
  riskScore: number
  reason: string | null
}

export interface RosterOfficer {
  officerId: string
  badge: string
  rank: string
  homeDivision: string
  certifications: string[]
  years: number
  availability: string
  assignedTo: string | null
}

export interface RosterVehicle {
  vehicleId: string
  unitNumber: string
  vehicleType: string
  baseDivision: string
  status: string
  availability: string
  assignedTo: string | null
}

export interface UnitType {
  code: string
  name: string
  requiredVehicleType: string
  minCrew: number
  maxCrew: number
  requiredCertifications: string[]
}

export interface ShiftRoster {
  unitTypes: UnitType[]
  officers: RosterOfficer[]
  vehicles: RosterVehicle[]
}

export interface Violation {
  ruleCode: string
  severity: 'Soft' | 'Hard' | 'Blocking'
  /** La frase en español que armó el servidor: respaldo si el front no conoce el código. */
  message: string
  args: Record<string, string> | null
}

export interface Evaluation {
  violations: Violation[]
  isBlocked: boolean
  isClean: boolean
}

export interface FormUnit {
  unitTypeCode: string
  officerIds: string[]
  vehicleId: string
  division: string
  callSign: string
  exceptions?: { ruleCode: string; reason: string }[]
}

export interface Vehicle {
  vehicleId: string
  unitNumber: string
  vehicleType: string
  baseDivision: string
  status: string
  equipment: string[]
}

export interface OfficerProfile {
  officerId: string
  badge: string
  rank: string
  homeDivision: string
  serviceStartDate: string
  years: number
  certifications: string[]
  isActive: boolean
}

export interface AvailabilityEntry {
  resourceId: string
  status: string
  reason: string
}

/**
 * Lo que el planificador pide al generar. **Lo que falta, lo pone el perfil vigente**: el servidor
 * solo reemplaza lo que venga. Mandar siempre todo pisaba en silencio lo que aprobó el mando
 * (hallazgo H-10 de la prueba integral).
 */
export interface GenerateScenario {
  preset?: string
  rule?: string
  revisitMinutes?: number
  hourly?: boolean
  lookahead?: boolean
  reserve?: boolean
}

/** Lo que hace el Patrol Scheduler (§7). */
export class TactixApi {
  constructor(private readonly token: () => string | null) {}

  shift = (date: string, code: string): Promise<ShiftSummary> =>
    this.call(`/api/tactix/v1/shifts/${date}/${code}`)

  /** Qué le toca a cada rol para el turno que se prepara (H-68). Sin turno por delante, nada. */
  readiness = async (shift?: { date: string; code: string }): Promise<Readiness | null> =>
    (await this.call<Readiness | undefined>(`/api/tactix/v1/readiness${shift ? `?date=${shift.date}&code=${encodeURIComponent(shift.code)}` : ''}`)) ?? null

  roster = (date: string, code: string): Promise<ShiftRoster> =>
    this.call(`/api/tactix/v1/shifts/${date}/${code}/roster`)

  evaluate = (date: string, code: string, r: FormUnit): Promise<Evaluation> =>
    this.call(`/api/tactix/v1/shifts/${date}/${code}/units/evaluate`, 'POST', r)

  formUnit = (date: string, code: string, r: FormUnit): Promise<unknown> =>
    this.call(`/api/tactix/v1/shifts/${date}/${code}/units`, 'POST', r)

  vehicles = (): Promise<Vehicle[]> => this.call('/api/tactix/v1/vehicles')

  vehicleStatus = (vehicleId: string, status: string): Promise<Vehicle> =>
    this.call(`/api/tactix/v1/vehicles/${vehicleId}/status`, 'POST', { status })

  /** Dónde ha andado un vehículo: en qué unidades, con quién y cuánto trabajó. */
  vehicleHistory = (vehicleId: string): Promise<VehicleHistory> =>
    this.call(`/api/tactix/v1/vehicles/${vehicleId}/history`)

  officers = (): Promise<OfficerProfile[]> => this.call('/api/tactix/v1/officers')

  officerActive = (officerId: string, isActive: boolean): Promise<unknown> =>
    this.call(`/api/tactix/v1/officers/${officerId}/active`, 'POST', { isActive })

  availability = (date: string, code: string, kind: string): Promise<AvailabilityEntry[]> =>
    this.call(`/api/tactix/v1/shifts/${date}/${code}/availability/${kind}`)

  publishAvailability = (date: string, code: string, kind: string, entries: AvailabilityEntry[]): Promise<unknown> =>
    this.call(`/api/tactix/v1/shifts/${date}/${code}/availability/${kind}`, 'PUT', entries)

  scenarios = (date: string, code: string): Promise<Scenario[]> =>
    this.call(`/api/tactix/v1/shifts/${date}/${code}/scenarios`)

  generate = (date: string, code: string, r: GenerateScenario): Promise<Scenario> =>
    this.call(`/api/tactix/v1/shifts/${date}/${code}/scenarios`, 'POST', r)

  propose = (scenarioId: string, note: string): Promise<unknown> =>
    this.call(`/api/tactix/v1/scenarios/${scenarioId}/propose`, 'POST', { note })

  /**
   * Pasa una visita a otra unidad, a mano (§9.4, §15.3). Publica una versión nueva del plan; la
   * justificación es obligatoria y queda en su historia.
   */
  reassign = (assignmentId: string, toPatrolUnitId: string, justification: string): Promise<ManualReassignment> =>
    this.call(`/api/tactix/v1/assignments/${assignmentId}/reassign`, 'POST', { toPatrolUnitId, justification })

  /** Retira una visita del plan sin dársela a nadie (H-128). El motivo es obligatorio. */
  cancelAssignment = (assignmentId: string, justification: string): Promise<ManualCancellation> =>
    this.call(`/api/tactix/v1/assignments/${assignmentId}/cancel`, 'POST', { justification })

  /** Devuelve una propuesta para correcciones. El motivo es obligatorio. */
  /**
   * La reoptimización que espera aprobación, con su delta (§15.1, §15.3), o nulo si no hay.
   * Es lo que el Centro de Mando muestra para decidir sin salir de él.
   */
  revision = async (date: string, code: string): Promise<PendingRevision | null> => {
    const response = await traced(`/api/tactix/v1/shifts/${date}/${code}/revision`, {
      headers: { Authorization: `Bearer ${this.token() ?? ''}` },
    })
    if (response.status === 204) return null
    if (!response.ok) throw new DeniedError(await denialOf(response))
    return (await response.json()) as PendingRevision
  }

  /** Los turnos de la agencia. Cualquiera de la agencia los puede leer. */
  shiftTemplates = (): Promise<ShiftTemplate[]> => this.call('/api/tactix/v1/shift-templates')

  reject = (planId: string, version: number, reason: string): Promise<unknown> =>
    this.call(`/api/tactix/v1/plans/${planId}/versions/${version}/reject`, 'POST', { reason })

  approve = (planId: string, version: number, acknowledgePartial: boolean): Promise<unknown> =>
    this.call(`/api/tactix/v1/plans/${planId}/versions/${version}/approve`, 'POST',
      { note: '', acknowledgePartial })

  /**
   * Sube la plantilla o la flota exportada del sistema de la agencia.
   *
   * Sin `Content-Type`: lo pone el navegador con la frontera del multipart, y fijarlo a mano
   * produce un cuerpo que el servidor no puede separar.
   */
  importRoster = async (kind: 'officers' | 'vehicles', file: File): Promise<RosterImport> => {
    const form = new FormData()
    form.append('file', file)
    const response = await traced(`/api/tactix/v1/roster/imports/${kind}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${this.token() ?? ''}` },
      body: form,
    })
    if (!response.ok) throw new DeniedError(await denialOf(response))
    return (await response.json()) as RosterImport
  }

  private async call<T>(path: string, method = 'GET', body?: unknown): Promise<T> {
    const response = await traced(path, {
      method,
      headers: {
        Authorization: `Bearer ${this.token() ?? ''}`,
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    })
    if (!response.ok) throw new DeniedError(await denialOf(response))
    // Un 204 no trae cuerpo; pedirle JSON revienta con un error que no dice nada.
    return (response.status === 204 ? undefined : await response.json()) as T
  }
}

/** Un paso del ciclo del turno que se prepara (H-68). */
export interface ReadinessStep {
  id: 'officers' | 'vehicles' | 'hotspots' | 'strategy' | 'units' | 'proposal' | 'approval'
  done: boolean
  /** La capacidad de quien lo hace. */
  capability: string
  count: number | null
  detail: string
}

export interface Readiness {
  shiftDate: string
  shiftCode: string
  start: string
  steps: ReadinessStep[]
}

/** Una fila que no entró, con dónde está y qué tiene. */
export interface RosterProblem {
  line: number
  code: string
  column: string
}

/** Lo que dejó una importación de plantilla o flota. */
export interface RosterImport {
  importId: string
  status: string
  read: number
  written: number
  rejected: number
  problems: RosterProblem[]
  /** Los que la agencia ya tenía y no venían en el archivo. **No se dieron de baja.** */
  absent: string[]
  /** Cuántos había registrados antes. Es lo que hace legible a `absent`. */
  known: number
  /** Columnas con datos personales que traía el archivo y no se guardaron. */
  ignoredPersonal?: string[]
  /** Columnas que no se reconocieron y no se usaron (H-82). */
  ignoredUnknown?: string[]
  /** Campos opcionales que el archivo no trae: a quien ya existía se le conservaron (H-84). */
  kept?: string[]
}

export interface Device {
  deviceId: string
  label: string
  status: string
  registeredAt: string
  revokedAt: string | null
  revokedReason: string
}

/**
 * Un turno de la agencia.
 *
 * Venían fijos en el producto —tres de ocho horas— y no cabía una corporación con turnos de doce
 * ni con uno de refuerzo. `hours` y `crossesMidnight` los calcula el servidor: si los mandara la
 * pantalla, podría mandarlos mal.
 */
/** Un distrito con límite cargado. */
export interface District {
  code: string
  name: string
}

export interface DistrictProblem {
  /** Posición en el archivo, desde uno. */
  index: number
  /** El código que traía, o vacío si el problema es que falta. */
  district: string
  /** `missing_code`, `not_an_area` o `duplicate`. */
  code: string
}

export interface DistrictImport {
  read: number
  written: number
  problems: DistrictProblem[]
  /** El número de esta carga. */
  version?: number
  /** Desde cuándo rige. */
  validFrom?: string | null
}

/** Una carga de distritos o de zonas, con su vigencia (§8.3). */
export interface GeoLayerVersion {
  version: number
  validFrom: string
  /** Nulo mientras rige. */
  validTo: string | null
  areas: number
  loadedBy: string
}

export interface ShiftTemplate {
  code: string
  name: string
  /** Hora local de inicio, `HH:mm`. */
  start: string
  end: string
  hours: number
  crossesMidnight: boolean
}

export interface StaffingTarget {
  division: string
  shiftCode: string
  positions: number
  oneOfficerUnits: number
  twoOfficerUnits: number
  sergeants: number
  units: number
}

export interface Jurisdiction {
  sector: string
  division: string
}

export interface DeviationThresholds {
  lateArrivalMinutes: number
  minPresenceRatio: number
  geofenceMeters: number
}

export interface PolicyChange {
  at: string
  effectiveFrom: string
  changedBy: string
  reason: string
  /** Lo que quedó fijado, en texto: respaldo para lo que la pantalla no sepa escribir. */
  values: string
  /** `deviation`, `formation` o `approval`, con lo fijado en datos: se escribe en el idioma y las unidades de quien lee (H-75). */
  kind?: string
  detail?: Record<string, string> | null
}

export interface DeviationPolicyView {
  current: DeviationThresholds
  /** Lo ya fijado que todavía no empieza a regir. */
  pending: DeviationThresholds | null
  pendingFrom: string | null
  /** Desde cuándo regiría un cambio guardado en este momento. */
  nextShiftStart: string
  history: PolicyChange[]
}

/** Las reglas con que se arma una tripulación en esta agencia (§9.3), con su historia. */
export interface FormationPolicyView {
  rookieThresholdYears: number
  /** Regla → Soft, Hard o Blocking. Solo las que la agencia gradúa. */
  severities: Record<string, string>
  /** Turno → certificaciones exigidas además de las del tipo de unidad. */
  requiredCertificationsByShift: Record<string, string[]>
  configurable: string[]
  history: PolicyChange[]
}

export interface ApprovalPolicyView {
  requiresApproval: boolean
  requireDifferentApprover: boolean
  history: PolicyChange[]
}

export interface RevisionItem { callSign: string; label: string; plannedArrivalAt: string; status: string }

/** Una reoptimización que espera aprobación, con su delta contra el plan activo (§15.3). */
export interface PendingRevision {
  /** Quien mira la pidió y la agencia exige cuatro ojos: la aprueba otra persona (H-39). */
  approverMustBeOther?: boolean
  /** Por qué ya no se puede publicar (el plan cambió desde la corrida), o nulo (H-40). */
  staleReason?: string | null
  /** Lo que la revisión deja sin visita, si es parcial (H-37). */
  uncovered?: UncoveredHotspot[]
  /** La revisión devuelta a la que esta es idéntica, si lo es (H-42). */
  sameAsReturned?: { version: number; returnedBy: string; returnedAt: string | null; note: string } | null
  planId: string
  version: number
  baseVersion: number
  requestedBy: string
  reason: string
  requestedAt: string
  isPartial: boolean
  coverageBefore: number
  coverageAfter: number
  frozen: number
  keptPlanned: number
  removed: RevisionItem[]
  cancelDisclosed: RevisionItem[]
  reassignInterrupted: RevisionItem[]
  added: RevisionItem[]
  excludedUnits: { callSign: string; status: string }[]
}

/** Un mensaje muerto, para decidir qué hacer con él (H-73). */
export interface DeadLetterItem {
  id: string
  messageType: string
  exceptionType: string
  exceptionMessage: string
  sentAt: string | null
}

export interface DeadLetterSummary {
  count: number
  latest: string | null
  types: string[]
}

export interface AuditEntry {
  at: string
  kind: string
  actor: string
  subject: string
  detail: string
  /** Si el sujeto es una forma de política y no un dato, su código: se traduce. */
  subjectCode: string | null
}

/** Lo que administra el Platform/Security Admin (§7). */
/**
 * `?validFrom=…` en UTC, o nada para «desde ahora». El campo de fecha y hora da una hora sin zona:
 * se lee en la hora que la pantalla está mostrando (la de la agencia, salvo que quien mira pida la
 * suya), la misma en que ve todas las demás (H-01). Antes se leía en la del navegador aunque la
 * pantalla mostrara otra.
 */
export const validFromQuery = (local?: string): string =>
  local ? `?validFrom=${encodeURIComponent(wallToIso(local))}` : ''

export class AdminApi {
  constructor(private readonly token: () => string | null) {}

  devices = (): Promise<Device[]> => this.call('/api/admin/v1/devices')

  registerDevice = (deviceId: string, label: string): Promise<Device> =>
    this.call('/api/admin/v1/devices', 'POST', { deviceId, label })

  revokeDevice = (deviceId: string, reason: string): Promise<Device> =>
    this.call(`/api/admin/v1/devices/${deviceId}/revoke`, 'POST', { reason })

  deviationPolicy = (): Promise<DeviationPolicyView> => this.call('/api/admin/v1/deviation-policy')

  saveDeviationPolicy = (p: DeviationThresholds & { reason: string }): Promise<unknown> =>
    this.call('/api/admin/v1/deviation-policy', 'PUT', p)

  approvalPolicy = (): Promise<ApprovalPolicyView> => this.call('/api/admin/v1/approval-policy')

  formationPolicy = (): Promise<FormationPolicyView> => this.call('/api/admin/v1/formation-policy')

  saveFormationPolicy = (p: {
    rookieThresholdYears: number; severities: Record<string, string>
    requiredCertificationsByShift: Record<string, string[]>; reason: string
  }): Promise<unknown> => this.call('/api/admin/v1/formation-policy', 'PUT', p)

  saveApprovalPolicy = (
    p: { requiresApproval: boolean; requireDifferentApprover: boolean; reason: string },
  ): Promise<unknown> => this.call('/api/admin/v1/approval-policy', 'PUT', p)

  districts = (): Promise<District[]> => this.call('/api/admin/v1/districts')

  /**
   * Sube los límites en GeoJSON. El cuerpo es el archivo tal cual, sin envolver en un formulario:
   * un GeoJSON es texto y lo exporta un GIS.
   */
  /** Las cargas de límites, de la más nueva a la más vieja. */
  districtVersions = (): Promise<GeoLayerVersion[]> => this.call('/api/admin/v1/districts/versions')

  saveDistricts = async (geoJson: string, validFrom?: string): Promise<DistrictImport> => {
    const response = await traced(`/api/admin/v1/districts${validFromQuery(validFrom)}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${this.token() ?? ''}`, 'Content-Type': 'application/geo+json' },
      body: geoJson,
    })
    if (!response.ok) throw new DeniedError(await denialOf(response))
    return (await response.json()) as DistrictImport
  }

  patrolZones = (): Promise<District[]> => this.call('/api/admin/v1/patrol-zones')

  /** Igual que los límites: el cuerpo es el GeoJSON tal cual. Es el mismo dato con otro propósito. */
  zoneVersions = (): Promise<GeoLayerVersion[]> => this.call('/api/admin/v1/patrol-zones/versions')

  savePatrolZones = async (geoJson: string, validFrom?: string): Promise<DistrictImport> => {
    const response = await traced(`/api/admin/v1/patrol-zones${validFromQuery(validFrom)}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${this.token() ?? ''}`, 'Content-Type': 'application/geo+json' },
      body: geoJson,
    })
    if (!response.ok) throw new DeniedError(await denialOf(response))
    return (await response.json()) as DistrictImport
  }

  shiftTemplates = (): Promise<ShiftTemplate[]> => this.call('/api/admin/v1/shift-templates')

  branding = (): Promise<Branding> => this.call('/api/admin/v1/branding')

  saveBranding = (b: BrandingChange): Promise<unknown> => this.call('/api/admin/v1/branding', 'PUT', b)

  /** El escudo va como el cuerpo, tal cual: el servidor mira qué imagen es por su contenido. */
  saveBadge = async (image: Blob): Promise<void> => {
    const response = await traced('/api/admin/v1/branding/badge', {
      method: 'PUT',
      headers: { Authorization: `Bearer ${this.token() ?? ''}`, 'Content-Type': image.type || 'application/octet-stream' },
      body: image,
    })
    if (!response.ok) throw new DeniedError(await denialOf(response))
  }

  removeBadge = (): Promise<unknown> => this.call('/api/admin/v1/branding/badge', 'DELETE')

  /** Reemplaza los turnos completos: quien no venga en la lista deja de existir. */
  saveShiftTemplates = (templates: ShiftTemplate[]): Promise<ShiftTemplate[]> =>
    this.call('/api/admin/v1/shift-templates', 'PUT', templates)

  staffingTargets = (): Promise<StaffingTarget[]> => this.call('/api/admin/v1/staffing-targets')

  saveStaffingTargets = (targets: StaffingTarget[]): Promise<unknown> =>
    this.call('/api/admin/v1/staffing-targets', 'PUT', targets)

  jurisdictions = (): Promise<Jurisdiction[]> => this.call('/api/admin/v1/jurisdictions')

  saveJurisdictions = (map: Jurisdiction[]): Promise<unknown> =>
    this.call('/api/admin/v1/jurisdictions', 'PUT', map)

  /** Mensajes que nadie procesó: trabajo que alguien cree hecho (hallazgo H-15). */
  deadLetters = (): Promise<DeadLetterSummary> => this.call('/api/admin/v1/dead-letters')
  deadLetterItems = (): Promise<DeadLetterItem[]> => this.call('/api/admin/v1/dead-letters/items')
  discardDeadLetter = (id: string, reason: string): Promise<unknown> =>
    this.call(`/api/admin/v1/dead-letters/${id}/discard`, 'POST', { reason })

  audit = (kind: string): Promise<AuditEntry[]> =>
    this.call(`/api/admin/v1/audit${kind ? `?kind=${kind}` : ''}`)

  private async call<T>(path: string, method = 'GET', body?: unknown): Promise<T> {
    const response = await traced(path, {
      method,
      headers: {
        Authorization: `Bearer ${this.token() ?? ''}`,
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    })
    if (!response.ok) throw new DeniedError(await denialOf(response))
    return (response.status === 204 ? undefined : await response.json()) as T
  }
}

/** Cómo le fue a un perfil sobre todos los turnos que se planificaron con él. */
export interface ProfileOutcome {
  profile: string
  from: string | null
  to: string | null
  shifts: number
  officersPerShift: number
  absentPerShift: number
  incidentsPerShift: number
  incidentMinutesPerShift: number
  planned: number
  released: number
  completed: number
  deferred: number
  lateArrivals: number
  shortPresence: number
  outOfZone: number
  connectivityLost: number
  onTimeRate: number | null
  presenceRate: number | null
  deferredPerShift: number
}

/** Una meta del perfil contra lo que de verdad pasó. */
export interface TargetCheck {
  target: string
  goal: number | null
  actual: number | null
  /** Nulo si el perfil no se puso esa meta o si no hay con qué medirla. */
  met: boolean | null
}

export interface ProfileScorecard {
  profile: StrategyProfile
  outcome: ProfileOutcome
  targets: TargetCheck[]
}

/** Dos perfiles uno al lado del otro (§11.1, Figura 5). */
export interface ProfileComparison {
  a: ProfileScorecard
  b: ProfileScorecard
}

/** Un turno con sus dos extremos. */
export interface ShiftWindow {
  shiftDate: string
  shiftCode: string
  start: string
  end: string
  remainingMinutes: number
}

/** Qué turno corre (§10). Se calcula del reloj y la zona de la agencia; no se guarda. */
export interface ShiftNow {
  now: string
  /** Nulo si en este momento no corre ninguno: una agencia puede dejar un hueco sin cobertura. */
  current: ShiftWindow | null
  previous: ShiftWindow | null
  next: ShiftWindow | null
  /** El que un supervisor entregaría ahora, para que nadie tenga que escribir una fecha. */
  toHandOver: ShiftWindow | null
  /**
   * Todos los que corren, el más reciente primero. Con turnos solapados son varios, y `current`
   * es el más reciente con plan publicado (H-34).
   */
  running: { window: ShiftWindow; hasPlan: boolean }[]
}

/** Un renglón de la bitácora operacional (§10.1). */
export interface TimelineEntry {
  at: string
  /** Código estable; la frase se arma aquí, en el idioma de quien mira. */
  kind: string
  callSign: string
  /** Vacío cuando lo detectó el sistema, que también es un dato. */
  actor: string
  /** Lo que escribió una persona. No se traduce: son sus palabras. */
  note: string
  args: Record<string, string>
}

export interface Timeline {
  from: string
  to: string
  shown: number
  entries: TimelineEntry[]
}

/** Lo que quedó abierto al entregar un turno (§10.1). */
export interface HandoverSnapshot {
  alerts: { alertId: string; kind: string; severity: string; callSign: string; status: string; createdAt: string }[]
  incidents: { incidentId: string; callSign: string; description: string; status: string; reportedAt: string }[]
  /** `reporting` es lo que contesta si a la unidad se le puede llamar. */
  units: { patrolUnitId: string; callSign: string; division: string; status: string; reporting: boolean }[]
  assignments: { assignmentId: string; callSign: string; label: string; status: string; plannedDepartureAt: string }[]
}

/** La entrega de un turno, con las dos firmas (§10.1). */
export interface Handover {
  handoverId: string
  shiftDate: string
  shiftCode: string
  status: 'Delivered' | 'Received'
  deliveredBy: string
  deliveredAt: string
  notes: string
  receivedBy: string
  receivedAt: string | null
  receiptNote: string
  /** La foto del instante del relevo, no la situación de ahora. */
  snapshot: HandoverSnapshot
}

/** Quién entró y qué puede hacer (§7). */
export interface Me {
  tenant: string
  agency: string
  subject: string
  roles: string[]
  capabilities: string[]
  /** El recuadro de la jurisdicción: donde abre el mapa. Nulo si la agencia no lo tiene. */
  agencyBounds?: MapArea | null
}

/** Un recuadro en grados. */
export interface MapArea { west: number; south: number; east: number; north: number }

/**
 * El mapa base, según la configuración del despliegue (§16.2).
 *
 * No va compilado: el servidor público de OpenStreetMap sirve para desarrollo y está prohibido en
 * producción, y cada agencia usa sus mosaicos o los de un proveedor con contrato.
 */
export interface MapBase {
  tileUrl: string; attribution: string; maxZoom: number
  /**
   * El mapa vectorial propio (mapas-servidor.sh): un estilo de MapLibre, claro y oscuro. Si viene,
   * manda sobre los mosaicos raster.
   */
  styleUrl?: string | null
  darkStyleUrl?: string | null
}

/** Lo que se usa mientras no llega la configuración: el mismo valor por omisión del servidor. */
export const DEFAULT_MAP_BASE: MapBase = {
  tileUrl: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '© OpenStreetMap', maxZoom: 19,
}

/**
 * Se le pregunta al backend, en vez de leer el token.
 *
 * Lo que vale es lo que el backend concede: una pantalla que decida por su cuenta se desincroniza
 * el día que cambie el mapa de roles, y lo hace en silencio.
 */
/**
 * Quién es el dueño del token. Nulo solo si el servidor lo rechaza (401/403); si no contesta
 * —la API reiniciándose, la red—, `'unreachable'`: eso no es una sesión vencida, y tratarlo así
 * mandaba al ingreso a quien recargaba la página durante un reinicio (prueba integral, 24-sep).
 */
export async function me(token: string): Promise<Me | null | 'unreachable' | { denied: Denial }> {
  try {
    const response = await traced('/api/me', { headers: { Authorization: `Bearer ${token}` } })
    if (response.ok) return (await response.json()) as Me
    // En el subdominio de otra agencia (AgencyHosts): hay que decir cuál es el suyo, no solo «no».
    if (response.status === 403) {
      const denial = await denialOf(response)
      if (denial.code === 'agency.other_host') return { denied: denial }
    }
    return response.status === 401 || response.status === 403 ? null : 'unreachable'
  } catch {
    return 'unreachable'
  }
}

export interface QualityFlag { flag: string; count: number }

export interface QualitySource {
  source: string
  count: number
  unusable: number
  first: string | null
  /** El incidente más reciente de esa fuente: es lo que dice si la fuente sigue viva. */
  last: string | null
}

export interface QualitySample {
  incidentId: string
  externalId: string
  offenseName: string
  occurredAt: string | null
  blockAddress: string
  status: 'Ok' | 'Review' | 'Unusable'
  flags: string[]
}

export interface QualityReport {
  total: number
  /** Los que pueden alimentar un cálculo de hotspots. */
  mappable: number
  ok: number
  review: number
  unusable: number
  flags: QualityFlag[]
  sources: QualitySource[]
  samples: QualitySample[]
}

export interface HistoricalIncident {
  incidentId: string
  occurredAt: string | null
  /** False cuando la fuente rellenó la hora: el análisis por franja no lo usa. */
  exact: boolean
  latitude: number | null
  longitude: number | null
  offenseCode: string
  offenseName: string
  tier: string
  beat: string
  blockAddress: string
  status: 'Ok' | 'Review' | 'Unusable'
}

export interface IncidentPage {
  /** Cuántos cumplen el filtro. Puede ser mucho mayor que los que se muestran. */
  matching: number
  shown: number
  incidents: HistoricalIncident[]
}

export interface IncidentFilter {
  from?: string
  to?: string
  /** Franja del día en hora de la agencia, como la del cálculo: `06-10` (H-89). */
  window?: string
  tier?: string
  quality?: string
  limit?: number
}

/** Cómo le fue a un turno ya ocurrido. No confundir con `ShiftSummary`, que es el de ahora. */
export interface ShiftPerformance {
  shiftDate: string
  shiftCode: string
  plannedUnits: number
  plannedAssignments: number
  released: number
  completed: number
  interrupted: number
  /** Las que pasaron su hora sin que la unidad llegara. Falta en un servidor anterior. */
  missed?: number
  /** Planeadas que nunca se liberaron: las «actividades diferidas» de §11.1. */
  deferred: number
  covered: number
  isPartial: boolean
  officersAvailable: number
  officersAbsent: number
  officersRestricted: number
  vehiclesAvailable: number
  lateArrivals: number
  shortPresence: number
  outOfZone: number
  connectivityLost: number
  /** Unidades que no iniciaron el turno con visitas vencidas (H-24). */
  shiftsNotStarted: number
  /** Unidades lejos de su jurisdicción (H-50). */
  outsideJurisdiction?: number
  /** Pedidos de apoyo, versiones del plan devueltas y ajustes manuales (H-129). */
  supportRequests?: number
  planVersionsReturned?: number
  manualAdjustments?: number
  /** Escenarios generados para el turno antes de aprobar (H-16). */
  scenariosGenerated?: number
  incidents: number
  incidentMinutes: number
  reoptimizationRuns: number
  reoptimizationsProposed: number
  /** Lo que tarda el motor por corrida. Nulo si no hubo corridas. */
  reoptimizationAverageMs: number | null
  /** Puntos de cobertura que ganaron las propuestas frente a no hacer nada. */
  coverageRecovered: number | null
  /** Nulo si nadie llegó todavía, que no es lo mismo que cero. */
  onTimeRate: number | null
  presenceRate: number | null
  approvedBy: string
}

export interface UnitPerformance {
  patrolUnitId: string
  callSign: string
  assignments: number
  completed: number
  averageLateMinutes: number | null
  planned: number
  presence: number
  deviations: number
  /** Las que pasaron su hora sin llegada. Falta en un servidor anterior. */
  missed?: number
}

export interface IncidentSummary {
  incidentId: string
  callSign: string
  description: string
  startedAt: string
  endedAt: string | null
  minutes: number | null
}

export interface ShiftReview {
  shift: ShiftPerformance
  units: UnitPerformance[]
  incidents: IncidentSummary[]
}

/** Los informes de INSIGHT: plan contra real. Solo lectura, y no deciden nada del turno de hoy. */
export class InsightApi {
  constructor(private readonly token: () => string | null) {}

  shifts = (): Promise<ShiftPerformance[]> => this.call('/api/insight/v1/shifts')

  review = (date: string, code: string): Promise<ShiftReview> =>
    this.call(`/api/insight/v1/shifts/${date}/${code}`)

  trends = (): Promise<Trends> => this.call('/api/insight/v1/trends')

  /** Rehace los hechos del periodo desde lo que guardaron TACTIX y SENTINEL. Borra y reconstruye. */
  rebuild = (): Promise<BackfillResult> => this.call('/api/insight/v1/rebuild', 'POST')

  /**
   * La hoja del periodo.
   *
   * Se pide con el token, como todo lo demás: un enlace suelto no lo lleva, y abrir la descarga
   * sin autenticación sería publicar el desempeño de una agencia a quien tenga la dirección.
   */
  async csv(): Promise<Blob> {
    const token = this.token()
    const response = await traced('/api/insight/v1/shifts.csv', {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!response.ok) throw new DeniedError(await denialOf(response))
    return await response.blob()
  }

  private async call<T>(path: string, method = 'GET'): Promise<T> {
    const token = this.token()
    const response = await traced(path, {
      method,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(method === 'GET' ? {} : { 'Content-Type': 'application/json' }),
      },
      body: method === 'GET' ? undefined : '{}',
    })
    if (response.ok) return (await response.json()) as T
    throw new DeniedError(await denialOf(response))
  }
}

export interface StrategyTargets {
  /** Fracción de llegadas dentro de la hora planeada. Nulo: no se fijó meta. */
  onTimeRate: number | null
  presenceRate: number | null
  maxDeferredPerShift: number | null
}

export interface StrategyProfile {
  profileId: string
  name: string
  version: number
  status: 'Draft' | 'Validated' | 'Active' | 'Retired'
  /** La división que rige, o vacío si rige toda la agencia. */
  division: string
  framework: string
  rationale: string
  hotspotMethod: string
  timeWindow: string
  topHotspots: number
  minIncidents: number
  trainingDays: number
  optimizerPreset: string
  /** Lo que la agencia cambió encima del preset, en JSON. */
  policyOverrides: string
  targets: StrategyTargets
  proposedBy: string
  proposedAt: string
  validatedBy: string
  validatedAt: string | null
  approvedBy: string
  approvedAt: string | null
  /** Lo que dijo quien lo devolvió a borrador. */
  note: string
}

export interface ProfileDraft {
  name: string
  /**
   * La división que rige, o vacío para toda la agencia.
   *
   * Un perfil de división no lleva método analítico y solo mueve cinco perillas: el servidor lo
   * rechaza si trae más, porque cambiarían la escala con la que se comparan las divisiones.
   */
  division: string
  framework: string
  rationale: string
  hotspotMethod: string
  timeWindow: string
  topHotspots: number
  minIncidents: number
  trainingDays: number
  optimizerPreset: string
  policyOverrides: string
  targets: StrategyTargets
}

/**
 * El perfil de estrategia operacional (§2.2, Figura 5).
 *
 * Tres actos de tres personas: el analista propone, el planificador valida, el mando aprueba.
 * El cliente no los mezcla porque el backend tampoco.
 */
export class StrategyApi {
  constructor(private readonly token: () => string | null) {}

  profiles = (): Promise<StrategyProfile[]> => this.call('/api/strategy/v1/profiles')

  propose = (draft: ProfileDraft): Promise<StrategyProfile> =>
    this.call('/api/strategy/v1/profiles', 'POST', draft)

  update = (profileId: string, draft: ProfileDraft): Promise<StrategyProfile> =>
    this.call(`/api/strategy/v1/profiles/${profileId}`, 'PUT', draft)

  act = (profileId: string, action: 'validate' | 'return' | 'approve' | 'retire', note: string):
  Promise<StrategyProfile> =>
    this.call(`/api/strategy/v1/profiles/${profileId}/${action}`, 'POST', { note })

  /** Cuál de dos perfiles funcionó mejor, con las condiciones en que trabajó cada uno. */
  comparison = (a: string, b: string): Promise<ProfileComparison> =>
    this.call(`/api/strategy/v1/profiles/comparison?a=${a}&b=${b}`)

  private async call<T>(path: string, method = 'GET', body?: unknown): Promise<T> {
    const token = this.token()
    const response = await traced(path, {
      method,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    })
    if (response.ok) return (await response.json()) as T
    throw new DeniedError(await denialOf(response))
  }
}

export interface TrendPoint {
  shiftDate: string
  shiftCode: string
  planned: number
  released: number
  completed: number
  deferred: number
  deviations: number
  onTimeRate: number | null
  presenceRate: number | null
}

export interface TrendTotals {
  shifts: number
  planned: number
  released: number
  completed: number
  deferred: number
  deviations: number
  incidents: number
  incidentMinutes: number
  onTimeRate: number | null
  presenceRate: number | null
}

export interface ShiftCodeTrend {
  shiftCode: string
  shifts: number
  planned: number
  deferred: number
  deviations: number
  onTimeRate: number | null
  presenceRate: number | null
}

export interface DivisionTrend {
  division: string
  assignments: number
  late: number
  shortPresence: number
  onTimeRate: number | null
  presenceRate: number | null
}

export interface ProfileTrend {
  profile: string
  shifts: number
  planned: number
  /** Nulo si el perfil rigió solo una división de algún turno (H-130): no se puede partir. */
  deferred: number | null
  deviations: number | null
  onTimeRate: number | null
  presenceRate: number | null
}

export interface DeviationShare { kind: string; count: number }

/** Un punto al que se llega tarde una y otra vez: casi siempre está mal puesto en el plan. */
export interface RecurringSpot {
  label: string
  visits: number
  late: number
  averageLateMinutes: number
  /**
   * Qué se observa detrás de las llegadas tarde, de más a menos casos.
   *
   * **Hipótesis con evidencia, no causas demostradas.** El producto no publica afirmaciones
   * causales sin metodología acordada; esto dice qué se observa y en cuántos casos.
   */
  causes: SpotCause[]
}

export interface SpotCause {
  /** `late_release`, `travel_underestimated`, `first_of_shift`, `one_shift`. */
  code: string
  cases: number
  /** El número que la hace concreta —minutos—, o cero si la causa no tiene magnitud. */
  magnitude: number
}

export interface UnitTrend {
  callSign: string
  shifts: number
  assignments: number
  late: number
  shortPresence: number
  averageLateMinutes: number | null
}

export interface Trends {
  from: string
  to: string
  totals: TrendTotals
  /** El periodo anterior, del mismo largo. Nulo si no hay nada antes con qué comparar. */
  previous: TrendTotals | null
  series: TrendPoint[]
  byShiftCode: ShiftCodeTrend[]
  byDivision: DivisionTrend[]
  byProfile: ProfileTrend[]
  deviations: DeviationShare[]
  spots: RecurringSpot[]
  units: UnitTrend[]
}

/** Lo que dejó una reconstrucción. `ignored` no es un error: son eventos que a INSIGHT no le tocan. */
export interface BackfillResult {
  shifts: number
  applied: number
  ignored: number
}

/** Qué sabe hacer este despliegue: la misma compilación sirve para producción y para una demo. */
export interface Capabilities {
  demo: boolean
  /** Cómo ingresar de verdad, o nulo si este despliegue todavía no tiene proveedor de identidad. */
  oidc: { authority: string; clientId: string; audience: string | null } | null
  /** El contrato con el cliente (ADR-0004). Se muestra: una licencia que sorprende es un incidente. */
  license: LicenseView
  /** Cómo está protegida la base en reposo, según lo declaró quien instaló. */
  storage: StorageView
  /** Si se exige segundo factor (H-67): required, provider u off. Falta en un servidor anterior. */
  mfa?: 'required' | 'provider' | 'off'
  /** El mapa base. Puede faltar en un servidor anterior a este campo. */
  map?: MapBase
  /** La agencia de esta dirección, si es el subdominio de una (AgencyHosts). */
  agency?: { tenantId: string; agencyId: string } | null
  /**
   * Si el servidor contestó. No viene de él: lo pone la página. Sin respuesta no se sabe si hay
   * proveedor, y decir «no hay» mandaba a buscar un problema que no existe (H-58).
   */
  reachable: boolean
}

/**
 * Cómo se presenta la agencia antes de que nadie ingrese (§16.3d): nombre, lema, colores y escudo,
 * configurables por cada departamento. Sin agencia conocida, `agencyName` viene vacío y se muestra
 * PROTECT a secas.
 */
export interface Branding {
  tenantId: string
  agencyId: string
  agencyName: string
  motto: string
  primaryColor: string
  accentColor: string
  hasBadge: boolean
  badgeVersion: number
  center: { latitude: number; longitude: number } | null
  bounds: { west: number; south: number; east: number; north: number } | null
  /** La zona de la agencia (IANA): las horas se muestran en ella por omisión (H-01). */
  timeZoneId?: string
  /** Las unidades en que se leen las distancias (H-64): la que rige y la elegida (vacía: por el país). */
  unitSystem?: 'metric' | 'imperial'
  unitSystemChosen?: '' | 'metric' | 'imperial'
  /** El aviso de agencia de demostración; vacío en una agencia real. */
  demoNotice?: string
}

/** Lo de PROTECT, mientras llega lo de la agencia o si no se sabe cuál es. */
export const PRODUCT_BRANDING: Branding = {
  tenantId: '', agencyId: '', agencyName: '', motto: '', primaryColor: '#0A1628', accentColor: '#1EB6FF',
  hasBadge: false, badgeVersion: 0, center: null, bounds: null,
}

const brandingQuery = (tenantId: string, agencyId: string) =>
  `tenant=${encodeURIComponent(tenantId)}&agency=${encodeURIComponent(agencyId)}`

/** La marca de la agencia, sin ingreso. Si algo falla, la de PROTECT: el ingreso nunca queda en blanco. */
export async function branding(tenantId: string, agencyId: string): Promise<Branding> {
  try {
    const response = await traced(`/api/branding?${brandingQuery(tenantId, agencyId)}`)
    return response.ok ? ((await response.json()) as Branding) : PRODUCT_BRANDING
  } catch {
    return PRODUCT_BRANDING
  }
}

/**
 * La marca de quien ya ingresó, por su token: con el proveedor de identidad, la agencia la dice el
 * token y no lo que recuerde el navegador. Si falla, nulo: la barra sigue con el identificador.
 */
export async function myBranding(token: string): Promise<Branding | null> {
  try {
    const response = await traced('/api/branding/mine', { headers: { Authorization: `Bearer ${token}` } })
    return response.ok ? ((await response.json()) as Branding) : null
  } catch {
    return null
  }
}

/** Lo que Administración cambia del tema de la agencia. */
export interface BrandingChange {
  displayName: string
  motto: string
  primaryColor: string
  accentColor: string
  /** Vacío: por el país de la agencia (H-64). */
  unitSystem?: '' | 'metric' | 'imperial'
  /** El aviso de agencia de demostración; vacío la vuelve real. */
  demoNotice?: string
}

/** Dónde está el escudo; la versión evita que el navegador muestre uno anterior. */
export const badgeUrl = (tenantId: string, agencyId: string, version: number) =>
  `/api/branding/badge?${brandingQuery(tenantId, agencyId)}&v=${version}`

export interface StorageView {
  encryptionAtRest: 'undeclared' | 'none' | 'volume' | 'managed'
  encrypted: boolean
}

const sinCifrado: StorageView = { encryptionAtRest: 'undeclared', encrypted: false }

export interface LicenseView {
  state: 'Evaluation' | 'Active' | 'Grace' | 'Expired' | 'Invalid'
  soldTo: string
  modules: string[]
  maxPatrolUnits: number
  notAfter: string | null
  daysToExpiry: number | null
  /** Lo decide el backend: sin licencia válida, o con el vencimiento a la vista. */
  warn: boolean
}

const sinLicencia: LicenseView = {
  state: 'Evaluation', soldTo: '', modules: [], maxPatrolUnits: 0,
  notAfter: null, daysToExpiry: null, warn: true,
}

export async function capabilities(): Promise<Capabilities> {
  try {
    const response = await traced('/api/capabilities')
    return response.ok
      ? { ...((await response.json()) as Capabilities), reachable: true }
      : { demo: false, oidc: null, license: sinLicencia, storage: sinCifrado, reachable: false }
  } catch {
    return { demo: false, oidc: null, license: sinLicencia, storage: sinCifrado, reachable: false }
  }
}

/** A quién avisar cuando el servidor dice que la sesión venció. */
let alVencer: (() => void) | null = null

/**
 * Lo que hace la aplicación cuando la sesión vence, sea cual sea la pantalla que se enteró. Antes
 * solo lo hacía `guard`: lo que no pasa por él —importar la plantilla, subir capas— decía «vuelva a
 * ingresar» y dejaba a la persona en una pantalla que ya no podía hacer nada.
 */
export function onSessionExpired(accion: (() => void) | null) {
  alVencer = accion
}

/** Para quien se entera del 401 fuera de `fetch` (el canal en tiempo real). */
export function sessionExpired() {
  alVencer?.()
}

async function denialOf(response: Response): Promise<Denial> {
  const reference = response.headers.get(CorrelationHeader) ?? undefined
  // 401 no trae cuerpo: el token venció o no sirve.
  if (response.status === 401) {
    alVencer?.()
    return { code: 'app.session_expired', reason: '', reference }
  }
  // Un 5xx sin código es que el servidor contestó y falló: decir «sin conexión» mandaba a revisar
  // la red cuando lo que hace falta es la referencia para soporte (H-80).
  const sinCodigo = response.status >= 500 ? 'app.server_error'
    // Un 403 sin cuerpo es «no le toca», no «no hay conexión».
    : response.status === 403 ? 'app.forbidden' : 'app.unreachable'
  try {
    const body = (await response.json()) as Denial
    if (body?.code) return { ...body, reference }
    return { code: sinCodigo, reason: `HTTP ${response.status}`, reference }
  } catch {
    return { code: sinCodigo, reason: `HTTP ${response.status}`, reference }
  }
}

/** La cabecera con la que el servidor devuelve el identificador de la acción. */
const CorrelationHeader = 'X-Correlation-Id'

/**
 * Una cabecera «traceparent» nueva (W3C Trace Context), una por acción.
 *
 * El servidor adopta ese identificador para la petición, lo devuelve en X-Correlation-Id y lo
 * estampa en todo lo que la acción dispare por la cola: con un solo número se sigue la acción
 * entera. Se genera aquí y no se deja al servidor porque así también sirve cuando la respuesta no
 * llega a leerse.
 */
export function newTraceparent(): string {
  const hex = (bytes: number) =>
    Array.from(crypto.getRandomValues(new Uint8Array(bytes)), (b) => b.toString(16).padStart(2, '0')).join('')
  return `00-${hex(16)}-${hex(8)}-01`
}

/** `fetch` con su traceparent. Toda llamada al servidor pasa por aquí. */
function traced(input: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers)
  headers.set('traceparent', newTraceparent())
  return fetch(input, { ...init, headers })
}
